/**
 * CauseReportTile - Mini treemap + top 3 causes, links to full cause report
 *
 * Atomic level: Molecule
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../../ui/card';
import { RootCausesTreemap } from './RootCausesTreemap';
import type { RootCauseCategory } from '../../../../types/record';
import type { LabelCountItem } from '../../../../services/types/collection';

interface CauseReportTileProps {
  collectionId: string;
  rootCauses: RootCauseCategory[];
  topLabels?: LabelCountItem[];
  className?: string;
}

const CauseReportTile: React.FC<CauseReportTileProps> = ({
  collectionId,
  rootCauses,
  topLabels = [],
  className = '',
}) => {
  const listItems = topLabels.slice(0, 3);

  return (
    <Link to={`/v4/reports/cause/${collectionId}`} className="block h-full">
      <Card
        className={`bg-white border-slate-200 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all h-full ${className}`}
      >
        <CardContent className="p-4 flex flex-col gap-3">
          <h3 className="text-[13px] font-bold">Cause Report</h3>

          {/* Mini treemap */}
          <div className="max-h-[120px] overflow-hidden">
            <RootCausesTreemap data={rootCauses} />
          </div>

          {/* Top root-cause labels with raw counts */}
          {listItems.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {listItems.map((item, i) => (
                <div key={item.label} className="flex items-center gap-2 text-xs">
                  <span className="w-[18px] h-[18px] rounded-full bg-muted flex items-center justify-center text-[9px] font-bold text-muted-foreground shrink-0">
                    {i + 1}
                  </span>
                  <span className="flex-1 font-medium text-foreground truncate">
                    {item.label}
                  </span>
                  <span className="font-bold text-foreground">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export { CauseReportTile };
export type { CauseReportTileProps };
